import {
  Button,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  SPACING,
  mediaTabletAndUp,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

/**
 * Copy and CTA laid over the bottom of a story media tile, on a darkening
 * gradient. Renders nothing when there is nothing to show. Absolutely
 * positioned, so it has to sit inside a `position: relative` tile frame.
 */
export const StoryTileOverlay: React.FC<{
  headline?: string;
  subline?: string;
  cta?: {text: string; url: string};
  testId: string;
}> = ({headline, subline, cta, testId}) => {
  if (!headline && !subline && !cta) return null;

  return (
    <>
      <Gradient aria-hidden="true" />

      <Overlay>
        {(headline || subline) && (
          <TextGroup>
            {subline && (
              <Eyebrow data-testid={`${testId}Subline`}>{subline}</Eyebrow>
            )}
            {headline && (
              <Headline data-testid={`${testId}Headline`}>{headline}</Headline>
            )}
          </TextGroup>
        )}

        {cta && (
          <CtaButton
            href={cta.url}
            variant="glassy"
            size="fit-width"
            data-testid={`${testId}CTA`}
          >
            {cta.text}
          </CtaButton>
        )}
      </Overlay>
    </>
  );
};

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.3) 100%
  );
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING['Y.Lg']};
  padding: ${SPACING['Y.Xl']} ${SPACING['X.Lg']} ${SPACING['Y.Xl2']};
  pointer-events: none;

  @media ${mediaTabletAndUp} {
    gap: ${SPACING['Y.Xl']};
  }
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Xs']};
  width: 100%;
  color: ${COLORS.White};
  text-align: center;
`;

const Eyebrow = styled.span`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 18px;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  @media ${mediaTabletAndUp} {
    font-size: ${FONT_SIZES.px14};
    line-height: 20px;
  }
`;

const Headline = styled.h2`
  margin: 0;
  font-size: ${FONT_SIZES.px32};
  font-weight: ${FONT_WEIGHTS.w700};
  line-height: 40px;
  letter-spacing: 0;
  word-break: break-word;

  @media ${mediaTabletAndUp} {
    font-size: ${FONT_SIZES.px40};
    line-height: 44px;
  }
`;

const CtaButton = styled(Button)`
  pointer-events: auto;
`;
